// web-commands.ts — the PER-APP half of the web shape: maps interview_prep_tutor's Tauri
// data commands (normally served by ipc.rs/db.rs over native SQLite) to the shared browser
// WebStore. Plain CRUD is a few lines each; the derived aggregates (session counts, theme
// counts) are faithfully re-implemented in JS since they're simple counting.
// The dormant native-only OCR command degrades to an empty result on web.

import type { CommandHandler, WebStore } from "./web-runtime";
import { browserSearch, browserFetch } from "./web-search-browser";

const SESSIONS = "sessions";
const QUESTIONS = "session_questions";
const BEST = "best_answers";
const PROFILE = "profile";

function uuid(): string {
  // crypto.randomUUID is available in every modern browser.
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}

/** Normalize a tag — lowercase, trim, collapse internal whitespace (mirrors db.rs::normalize). */
function normalizeTag(raw: string): string {
  return String(raw).trim().toLowerCase().split(/\s+/).filter(Boolean).join(" ");
}

function validSeniority(s: unknown): string {
  const v = normalizeTag(String(s ?? ""));
  return v === "junior" || v === "mid" || v === "senior" || v === "lead" ? v : "mid";
}
function validType(s: unknown): string {
  const v = normalizeTag(String(s ?? ""));
  return v === "behavioral" || v === "technical" || v === "mixed" ? v : "mixed";
}
function validMode(s: unknown): string {
  const v = normalizeTag(String(s ?? ""));
  return v === "prep" || v === "simulate" ? v : "prep";
}

type Row = Record<string, unknown> & { id: string };

/** Attach the computed summary fields (question/answered counts + distinct themes). */
function summarize(db: WebStore, session: Row): Row {
  const qs = db.all(QUESTIONS).filter((q) => (q as { session_id?: string }).session_id === session.id);
  const answered = qs.filter((q) => (q as { answered_at?: string | null }).answered_at);
  const seen = new Set<string>();
  const themes: string[] = [];
  for (const q of qs) {
    for (const t of String((q as { themes?: string }).themes ?? "").split(",")) {
      const n = normalizeTag(t);
      if (n && !seen.has(n)) { seen.add(n); themes.push(n); }
    }
  }
  // `report_json: ""` BEFORE the spread = the web mirror of db.rs's idempotent
  // ensure_column default — rows created before the column existed read as "".
  return { report_json: "", ...session, question_count: qs.length, answered_count: answered.length, themes };
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    // ── Sessions ───────────────────────────────────────────────────────────────
    list_sessions: ({ limit }) => {
      const rows = db.all(SESSIONS).sort((a, b) =>
        String((b as { created_at?: string }).created_at ?? "").localeCompare(
          String((a as { created_at?: string }).created_at ?? ""),
        ),
      ) as Row[];
      const n = Number(limit);
      const sliced = Number.isFinite(n) && n > 0 ? rows.slice(0, n) : rows;
      return sliced.map((r) => summarize(db, r));
    },
    get_session: ({ id }) => {
      const row = db.get(SESSIONS, String(id)) as Row | null;
      return row ? summarize(db, row) : null;
    },
    create_session: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const row: Row = {
        id: uuid(),
        role: String(i.role ?? "").trim(),
        seniority: validSeniority(i.seniority),
        interview_type: validType(i.interview_type),
        mode: validMode(i.mode),
        company: String(i.company ?? "").trim(),
        focus: String(i.focus ?? "").trim(),
        brief: String(i.brief ?? "").trim(),
        report_json: "",
        created_at: nowIso(),
        updated_at: nowIso(),
      };
      db.insert(SESSIONS, row);
      const qs = Array.isArray(i.questions) ? i.questions : [];
      qs.forEach((q, idx) => {
        const question = String((q as { question?: unknown })?.question ?? "").trim();
        if (!question) return;
        db.insert(QUESTIONS, {
          id: uuid(),
          session_id: row.id,
          idx,
          question,
          hint: String((q as { hint?: unknown })?.hint ?? "").trim(),
          answer: "",
          feedback_json: "",
          themes: "",
          answer_seconds: 0,
          answered_at: null,
          created_at: nowIso(),
        });
      });
      return summarize(db, row);
    },
    delete_session: ({ id }) => {
      db.remove(SESSIONS, String(id));
      // ON DELETE CASCADE on native clears the session's questions; mirror that here.
      for (const q of db.all(QUESTIONS)) {
        if ((q as { session_id?: string }).session_id === String(id)) db.remove(QUESTIONS, q.id);
      }
      return null;
    },

    // ── Questions / answers ────────────────────────────────────────────────────
    list_questions: ({ sessionId }) => {
      const rows = db
        .all(QUESTIONS)
        .filter((q) => (q as { session_id?: string }).session_id === String(sessionId));
      return rows.sort((a, b) => Number((a as { idx?: number }).idx ?? 0) - Number((b as { idx?: number }).idx ?? 0));
    },
    record_answer: ({ questionId, answer, feedbackJson, themes, answerSeconds }) => {
      const joined = (Array.isArray(themes) ? themes : [])
        .map((t) => normalizeTag(String(t)))
        .filter(Boolean)
        .join(",");
      const secs = Math.max(0, Math.round(Number(answerSeconds) || 0));
      const prev = db.get(QUESTIONS, String(questionId)) as { answer_seconds?: number } | null;
      db.update(QUESTIONS, String(questionId), {
        answer: String(answer ?? "").trim(),
        feedback_json: String(feedbackJson ?? ""),
        themes: joined,
        // Only overwrite when > 0 (a later coaching pass keeps the original time) — mirrors db.rs.
        answer_seconds: secs > 0 ? secs : Number(prev?.answer_seconds ?? 0),
        answered_at: nowIso(),
      });
      const q = db.get(QUESTIONS, String(questionId)) as { session_id?: string } | null;
      if (q?.session_id) db.update(SESSIONS, q.session_id, { updated_at: nowIso() });
      return null;
    },

    // Store the AI session report JSON on its session (mirrors db.rs::record_report).
    record_report: ({ sessionId, reportJson }) => {
      db.update(SESSIONS, String(sessionId), {
        report_json: String(reportJson ?? ""),
        updated_at: nowIso(),
      });
      return null;
    },

    // ── Profile — small on-device key/value store (mirrors db.rs; e.g. the resume) ─
    get_profile: ({ key }) => {
      const row = db.get(PROFILE, normalizeTag(String(key ?? ""))) as { value?: string } | null;
      return row ? String(row.value ?? "") : null;
    },
    set_profile: ({ key, value }) => {
      const k = normalizeTag(String(key ?? ""));
      if (!k) throw new Error("key_required");
      const v = String(value ?? "");
      if (!v.trim()) {
        db.remove(PROFILE, k);
        return null;
      }
      if (db.get(PROFILE, k)) db.update(PROFILE, k, { value: v, updated_at: nowIso() });
      else db.insert(PROFILE, { id: k, key: k, value: v, updated_at: nowIso() });
      return null;
    },

    // ── Best answers — the kept library (mirrors db.rs; ONE best answer per question) ─
    list_best_answers: () =>
      (db.all(BEST) as Row[]).sort((a, b) =>
        String((b as { updated_at?: string }).updated_at ?? "").localeCompare(
          String((a as { updated_at?: string }).updated_at ?? ""),
        ),
      ),
    save_best_answer: ({ question, answer, role, note }) => {
      const qText = String(question ?? "").trim();
      const aText = String(answer ?? "").trim();
      if (!qText || !aText) throw new Error("question_and_answer_required");
      const existing = (db.all(BEST) as Row[]).find((b) => String((b as { question?: string }).question ?? "") === qText);
      if (existing) {
        db.update(BEST, existing.id, {
          answer: aText,
          role: String(role ?? "").trim(),
          note: String(note ?? "").trim(),
          updated_at: nowIso(),
        });
        return db.get(BEST, existing.id);
      }
      const row: Row = {
        id: uuid(),
        question: qText,
        answer: aText,
        role: String(role ?? "").trim(),
        note: String(note ?? "").trim(),
        created_at: nowIso(),
        updated_at: nowIso(),
      };
      db.insert(BEST, row);
      return row;
    },
    delete_best_answer: ({ id }) => {
      db.remove(BEST, String(id));
      return null;
    },

    // ── Derived aggregate — feedback-theme counts (mirrors db.rs::theme_counts) ─
    theme_counts: () => {
      const counts = new Map<string, number>();
      for (const q of db.all(QUESTIONS)) {
        for (const t of String((q as { themes?: string }).themes ?? "").split(",")) {
          const n = normalizeTag(t);
          if (n) counts.set(n, (counts.get(n) ?? 0) + 1);
        }
      }
      return [...counts.entries()]
        .map(([theme, count]) => ({ theme, count }))
        .sort((a, b) => b.count - a.count || a.theme.localeCompare(b.theme));
    },

    // ── Native-only (Apple Vision OCR). Web returns empty → the PhotoButton explains
    // it can't scan on this surface and asks for pasted text instead. ─
    save_page_photo: () => "",
    delete_page_photo: () => null,
    ocr_image: () => "",
  };
}
